import type { CompactDhtmlxCalendar } from '../api/schedulingApis';

/** Interim global calendar when task.calendarId is null (until FBT standard calendar). */
export const INTERIM_GLOBAL_WORK_HOURS = ['08:00-12:00', '13:00-17:00'] as const;

export type GanttCalendarApi = {
  config?: { work_time?: boolean };
  setWorkTime?: (config: { day?: number; date?: Date; hours: boolean | string[] }) => void;
  addCalendar?: (calendar: {
    id?: string;
    worktime: CompactDhtmlxCalendar['worktime'];
  }) => string;
  getCalendar?: (id: string) => { id?: string } | null;
  deleteCalendar?: (id: string) => void;
  getCalendars?: () => Array<{ id?: string }>;
  render?: () => void;
};

/**
 * Apply Mon–Fri Standard hours on the DHTMLX global calendar.
 * Used for tasks with null calendarId until Falconbrick standard calendar exists.
 */
export function applyInterimGlobalWorkTime(gantt: GanttCalendarApi): void {
  if (!gantt.config || !gantt.setWorkTime) return;
  gantt.config.work_time = true;
  gantt.setWorkTime({ day: 0, hours: false });
  gantt.setWorkTime({ day: 6, hours: false });
  for (let day = 1; day <= 5; day++) {
    gantt.setWorkTime({ day, hours: [...INTERIM_GLOBAL_WORK_HOURS] });
  }
}

/**
 * Register project calendars from the compact DHTMLX endpoint.
 * Backend shape is pass-through (weekdays[0]=Sunday).
 */
export function registerProjectDhtmlxCalendars(
  gantt: GanttCalendarApi,
  calendars: CompactDhtmlxCalendar[],
): void {
  if (!gantt.addCalendar) return;

  const existing = gantt.getCalendars?.() ?? [];
  for (const cal of existing) {
    const id = cal?.id != null ? String(cal.id) : '';
    if (!id || id === 'global') continue;
    try {
      gantt.deleteCalendar?.(id);
    } catch {
      // Ignore missing calendars on re-register.
    }
  }

  for (const calendar of calendars) {
    try {
      gantt.addCalendar({
        id: calendar.id,
        worktime: calendar.worktime,
      });
    } catch (err) {
      console.warn('[Gantt] Failed to addCalendar', calendar.id, err);
    }
  }
}
